import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC, FormEvent } from 'react';
import { useState } from 'react';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuth } from '../../utils/auth/index.js';
import { getSharedDevice } from '../../utils/shared-device.js';

export interface SignInData {
  email: string;
  password: string;
  remember: boolean;
}

interface ViewProps<T> {
  data: T;
  failed: boolean;
  forgotPassword: () => void;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  sharedDevice: boolean;
}

export const SignIn: FC<{
  view?: FC<ViewProps<SignInData>>;
  forgotPassword: () => void;
}> = ({ view: View = SignInView, forgotPassword }) => {
  useSetTitle('Sign In');
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const [failed, setFailed] = useState(false);

  const formik = useFormik<SignInData>({
    initialValues: { email: '', password: '', remember: true },
    // TODO validate,
    onSubmit: (values) => {
      setFailed(false);
      auth
        .signIn(
          values.email,
          values.password,
          values.remember,
          getSharedDevice(),
        )
        .then((success) => setFailed(!success))
        .catch((err) => {
          console.log(err);
          Sentry.captureException(err);
          enqueueSnackbar(String(err), { variant: 'error' });
          setFailed(true);
        });
    },
  });

  return (
    <View
      data={formik.values}
      failed={failed}
      forgotPassword={() => {
        auth.signOut().catch(console.log);
        forgotPassword();
      }}
      handleChange={formik.handleChange}
      handleSubmit={formik.handleSubmit}
      sharedDevice={getSharedDevice()}
    />
  );
};

export const SignInView: FC<ViewProps<SignInData>> = ({
  data,
  failed,
  forgotPassword,
  handleChange,
  handleSubmit,
  sharedDevice,
}) => (
  <form onSubmit={handleSubmit}>
    <Box
      sx={{
        mx: 'auto',
        width: 200,
      }}
    >
      <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h4">
        Sign In
      </Typography>
    </Box>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          error={failed}
          fullWidth
          id="email"
          label="Email address"
          name="email"
          onChange={handleChange}
          required
          type="email"
          value={data.email}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          error={failed}
          fullWidth
          helperText={failed ? 'Invalid credentials' : undefined}
          id="password"
          label="Password"
          name="password"
          onChange={handleChange}
          required
          type="password"
          value={data.password}
        />
      </Grid>
      {!sharedDevice ? (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.remember}
                id="remember"
                onChange={handleChange}
              />
            }
            label="Stay signed in"
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <Box
          sx={{
            mx: 'auto',
            width: 150,
          }}
        >
          <Button
            fullWidth
            size="large"
            sx={{
              borderRadius: 100,
            }}
            type="submit"
            variant="contained"
          >
            Sign In
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            mx: 'auto',
            width: 150,
          }}
        >
          <Button
            fullWidth
            onClick={forgotPassword}
            sx={{ textTransform: 'none' }}
          >
            Forgot password?
          </Button>
        </Box>
      </Grid>
    </Grid>
  </form>
);
